import { useRef, useState, useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";
import { UIThread } from "../../generated/core-api";
import { useWorkspace } from "../../workspace";
import { api } from "../../api";

interface ExitFeedbackState {
  threadId: string;
  threadTitle: string;
  workspaceId: string;
  lastAssistantIndex: number;
}

export function useThreadExitFeedback() {
  const { workspace_id: workspaceId } = useWorkspace();
  const params = useParams<{ threadId: string }>();
  const threadId = params.threadId;
  const queryClient = useQueryClient();
  const prevThreadIdRef = useRef<string | undefined>(threadId);

  const [promptData, setPromptData] = useState<ExitFeedbackState | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const remainingRef = useRef(0);
  const startedAtRef = useRef(0);

  useEffect(() => {
    const prevThreadId = prevThreadIdRef.current;
    prevThreadIdRef.current = threadId;

    if (!prevThreadId || prevThreadId === threadId) return;

    const oldThread = queryClient.getQueryData<UIThread>([
      "workspace",
      workspaceId,
      "thread",
      prevThreadId,
    ]);
    if (!oldThread) return;

    const lastAssistantIndex = oldThread.messages.reduce<number>(
      (lastIdx, msg, idx) => (msg.role === "assistant" ? idx : lastIdx),
      -1
    );
    if (lastAssistantIndex === -1) return;

    const hasFeedback = Object.values(oldThread.feedback ?? {}).some(
      (v) => v !== 0
    );
    if (hasFeedback) return;

    setPromptData({
      threadId: prevThreadId,
      threadTitle: oldThread.title || "Untitled thread",
      workspaceId,
      lastAssistantIndex,
    });

    clearTimeout(timerRef.current);
    remainingRef.current = 8000;
    startedAtRef.current = Date.now();
    timerRef.current = setTimeout(() => {
      setPromptData(null);
    }, 8000);
  }, [threadId, workspaceId, queryClient]);

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  const submitFeedback = useCallback(
    async (feedback: 1 | -1) => {
      if (!promptData) return;

      try {
        await api.setThreadMessageFeedback(
          promptData.workspaceId,
          promptData.threadId,
          promptData.lastAssistantIndex,
          { feedback }
        );

        queryClient.setQueryData<UIThread>(
          [
            "workspace",
            promptData.workspaceId,
            "thread",
            promptData.threadId,
          ],
          (current) => {
            if (!current) return current;
            return {
              ...current,
              feedback: {
                ...current.feedback,
                [promptData.lastAssistantIndex.toString()]: feedback,
              },
            };
          }
        );

        setPromptData(null);
        clearTimeout(timerRef.current);
      } catch (error) {
        console.error("Failed to submit exit feedback:", error);
      }
    },
    [promptData, queryClient]
  );

  const dismiss = useCallback(() => {
    setPromptData(null);
    clearTimeout(timerRef.current);
  }, []);

  const pauseAutoDismiss = useCallback(() => {
    clearTimeout(timerRef.current);
    const elapsed = Date.now() - startedAtRef.current;
    remainingRef.current = Math.max(0, remainingRef.current - elapsed);
  }, []);

  const resumeAutoDismiss = useCallback(() => {
    const ms = Math.max(remainingRef.current, 2000);
    startedAtRef.current = Date.now();
    remainingRef.current = ms;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setPromptData(null);
    }, ms);
  }, []);

  return {
    promptData,
    submitFeedback,
    dismiss,
    pauseAutoDismiss,
    resumeAutoDismiss,
  };
}
